import React from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material'; // Added Grid, Paper, CircularProgress

// Highcharts imports
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import HighchartsReact from 'highcharts-react-official';
import Treemap from 'highcharts/modules/treemap';
import Annotations from 'highcharts/modules/annotations';

// Initialize Highcharts modules
HighchartsMore(Highcharts);
SolidGauge(Highcharts);
Treemap(Highcharts);
Annotations(Highcharts);

// CSRDCharts component (copied and adapted from Csrd-benchmarking.js)
const CSRDCharts = ({
  externalAssurancePercent,
  euTaxonomyPercent,
  companiesByCountry,
  emissionsProfile,
  workforceAndBoardDiversity,
  iroVisualFormat, // Expects an array of { name, value, color }
  esrsStandardsMentioned,
  frameworkAlignment
}) => {

  // External Assurance solid gauge chart
  const externalAssuranceOptions = {
    chart: { type: "solidgauge", height: 250, backgroundColor: "transparent" },
    title: { text: "Companies With External Assurance", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left", x: 10, y: 20 },
    pane: { center: ["50%", "70%"], size: "100%", startAngle: -90, endAngle: 90, background: { backgroundColor: "#E0E0E0", innerRadius: "60%", outerRadius: "100%", shape: "arc" } },
    tooltip: { enabled: false },
    yAxis: { min: 0, max: 100, stops: [[1, "#6F9896"]], lineWidth: 0, tickWidth: 0, minorTickInterval: null, tickAmount: 2, labels: { y: 16, style: { fontSize: "10px", color: "#666" } } },
    plotOptions: { solidgauge: { dataLabels: { y: -30, borderWidth: 0, useHTML: true, format: '<div style="text-align:center"><span style="font-size:24px;font-weight:bold;color:#494949">{y}%</span></div>' } } },
    series: [{ name: "Assurance", data: [externalAssurancePercent || 0] }],
    credits: { enabled: false },
  };

  // Country comparison bar chart
  const countryComparisonOptions = {
    chart: { type: "column", height: 250, backgroundColor: "transparent" },
    title: { text: "Count Of Companies By Country", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    xAxis: { categories: companiesByCountry?.map(item => item.country) || [], labels: { style: { fontSize: "10px" } } },
    yAxis: { min: 0, title: { text: "" } },
    legend: { enabled: false },
    series: [{ name: "Companies", data: companiesByCountry?.map(item => item.count) || [], color: "#FAB430" }],
    credits: { enabled: false },
  };

  // CSRD Title full circle donut chart (Note: Csrd-benchmarking.js had a static 100% value, this might need to be dynamic if data exists)
  const csrdTitleOptions = {
    chart: { type: "pie", height: 250, backgroundColor: "transparent" },
    title: { text: "% Of Companies With Dedicated CSRD Title", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    plotOptions: { pie: { innerSize: "60%", dataLabels: { enabled: false }, showInLegend: false } },
    series: [{ name: "CSRD Title", data: [{ name: "With Title", y: 100, color: "#6F9896" }] }], // Static example, adjust if dynamic data source for this exists
    tooltip: { enabled: false },
    annotations: [{ labels: [{ point: { x: 30, y: 180 }, text: "10 (100%)", useHTML: false, backgroundColor: "transparent", borderWidth: 0, shadow: false, style: { color: "#494949", fontSize: "11px", fontWeight: "normal" } }], shapes: [{ type: "path", points: [{ x: 70, y: 185 }, { x: 120, y: 150 }], stroke: "#666", strokeWidth: 1, fill: "none" }] }],
    credits: { enabled: false },
  };

  // EU Taxonomy full circle donut chart
  const euTaxonomyOptions = {
    chart: { type: "pie", height: 250, backgroundColor: "transparent" },
    title: { text: "Company With EU Taxonomy Section", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    plotOptions: { pie: { innerSize: "60%", dataLabels: { enabled: false }, showInLegend: false } },
    series: [{ name: "EU Taxonomy", data: [{ name: "With Section", y: euTaxonomyPercent || 0, color: "#B4A34A" }] }],
    tooltip: { enabled: false },
    annotations: [{ labels: [{ point: { x: 30, y: 180 }, text: `10 (${euTaxonomyPercent || 0}%)`, useHTML: false, backgroundColor: "transparent", borderWidth: 0, shadow: false, style: { color: "#494949", fontSize: "11px", fontWeight: "normal" } }], shapes: [{ type: "path", points: [{ x: 70, y: 185 }, { x: 120, y: 150 }], stroke: "#666", strokeWidth: 1, fill: "none" }] }],
    credits: { enabled: false },
  };

  // Emissions Profile bar chart
  const emissionsProfileOptions = {
    chart: { type: "column", height: 250, backgroundColor: "transparent" },
    title: { text: "Emissions Profile", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    xAxis: { categories: emissionsProfile?.map(item => item.company) || [], labels: { style: { fontSize: "9px" } } },
    yAxis: { min: 0, title: { text: "Emissions", style: { fontSize: "10px" } } },
    legend: { itemStyle: { fontSize: "10px" } },
    series: [
      { name: "Scope 1 emissions", data: emissionsProfile?.map(item => item.scope1) || [], color: "#6D9157" },
      { name: "Scope 2 emissions", data: emissionsProfile?.map(item => item.scope2) || [], color: "#B3A249" },
      { name: "Scope 3 emissions", data: emissionsProfile?.map(item => item.scope3) || [], color: "#FAB430" },
    ],
    credits: { enabled: false },
  };

  const calculateMaxYAxis = (data) => {
    if (!data || data.length === 0) return 60;
    const maxBoard = Math.max(...data.map(item => item.board_female_pct || 0));
    const maxWorkforce = Math.max(...data.map(item => item.workforce_female_pct || 0));
    const maxValue = Math.max(maxBoard, maxWorkforce);
    return Math.ceil(maxValue / 10) * 10 || 60; // Ensure a default if maxValue is 0 or NaN
  };

  // Workforce & Board Diversity
  const diversityOptions = {
    chart: { type: "column", height: 250, backgroundColor: "transparent" },
    title: { text: "Workforce & Board Diversity", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    xAxis: { categories: workforceAndBoardDiversity?.map(item => item.company) || [], labels: { style: { fontSize: "9px" } } },
    yAxis: { min: 0, max: calculateMaxYAxis(workforceAndBoardDiversity), title: { text: "Percentage", style: { fontSize: "10px" } } },
    legend: { itemStyle: { fontSize: "10px" } },
    plotOptions: { column: { dataLabels: { enabled: false } } },
    series: [
      { name: "Board Diversity (Female)", data: workforceAndBoardDiversity?.map(item => item.board_female_pct) || [], color: "#9DB9B8", dataLabels: { enabled: true, style: { fontSize: "9px", fontWeight: "normal", color: "#494949", textOutline: "none" }, format: "{y}", y: -5 } },
      { name: "Workforce Diversity (Female)", data: workforceAndBoardDiversity?.map(item => item.workforce_female_pct) || [], color: "#407775", dataLabels: { enabled: true, style: { fontSize: "9px", fontWeight: "normal", color: "#494949", textOutline: "none" }, format: "{y}", y: -5 } },
    ],
    credits: { enabled: false },
  };

  // IRO Visual Format
  const iroVisualOptions = {
    chart: { type: "treemap", height: 250, backgroundColor: "transparent" },
    title: { text: "IRO Visual Format", style: { fontSize: "12px", fontWeight: "500", color: "#494949" }, align: "left" },
    // colorAxis is not used if points have their own color, which they do from DeepDivePage.js
    // colorAxis: { minColor: "#FFFFFF", maxColor: "#000000", visible: false },
    series: [{
      type: "treemap",
      layoutAlgorithm: "squarified",
      allowDrillToNode: false,
      animationLimit: 1000,
      dataLabels: { enabled: true, style: { fontSize: "10px", fontWeight: "bold", color: "black", textOutline: "none" }, format: "{point.name}<br/>{point.value}" },
      levelIsConstant: false,
      levels: [{ level: 1, dataLabels: { enabled: true, style: { fontSize: "10px", fontWeight: "bold", color: "black", textOutline: "none" }, format: "{point.name}<br/>{point.value}" }, borderWidth: 2, borderColor: "#ffffff" }],
      data: iroVisualFormat || [], // Expects data like [{ name: "...", value: X, color: "#..."}]
    }],
    tooltip: { pointFormat: "<b>{point.name}</b><br/>Count: <b>{point.value}</b>", style: { fontSize: "12px" } }, // Removed percentage as it might be confusing with pre-set colors
    credits: { enabled: false },
  };

  return (
    <>
      {/* First row - 4 charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={externalAssuranceOptions} /></Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={countryComparisonOptions} /></Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={csrdTitleOptions} /></Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={euTaxonomyOptions} /></Paper>
        </Grid>
      </Grid>

      {/* Second row - 3 charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={emissionsProfileOptions} /></Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={diversityOptions} /></Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p:1, boxShadow: 1 }}><HighchartsReact highcharts={Highcharts} options={iroVisualOptions} /></Paper>
        </Grid>
      </Grid>

      {/* Third row - 2 tables */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p:2, boxShadow: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: "#494949", fontWeight: "500" }}>ESRS Standards Mentioned</Typography>
            <Box sx={{ overflowX: "auto", maxHeight: 200 }}>
              <table style={{ width: "100%", fontSize: "11px", borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: "#407775" }}>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", border: '1px solid #ddd' }}>Company Name</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", border: '1px solid #ddd' }}>ESRS Standards Mentioned</th>
                  </tr>
                </thead>
                <tbody>
                  {esrsStandardsMentioned?.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FCF7EE" : "#DAECEC" }}>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.company}</td>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.esrs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p:2, boxShadow: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: "#494949", fontWeight: "500" }}>Alignment With Other Frameworks</Typography>
            <Box sx={{ overflowX: "auto", maxHeight: 200 }}>
              <table style={{ width: "100%", fontSize: "11px", borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: "#407775" }}>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap", border: '1px solid #ddd' }}>Company Name</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap", border: '1px solid #ddd' }}>GRI Alignment</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap", border: '1px solid #ddd' }}>SBTi Target</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap", border: '1px solid #ddd' }}>TCFD Referenced</th>
                  </tr>
                </thead>
                <tbody>
                  {frameworkAlignment?.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FCF7EE" : "#DAECEC" }}>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.company}</td>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.gri_alignment ? "Yes" : "No"}</td>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.sbti_target_mentioned ? "Yes" : "No"}</td>
                      <td style={{ padding: "6px 10px", color: "#494949", border: '1px solid #ddd' }}>{item.tcfd_referenced ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

const DeepDiveOverview = ({ formData, benchmarkData, kpiData, chartData, loading }) => {
  // Global loading is handled by DeepDivePage, but we can show a message if data isn't ready.
  const hasKpiData = kpiData && (kpiData.company_count !== undefined || kpiData.average_csrd_page_count !== undefined || kpiData.average_material_topics_count !== undefined);
  const hasChartData = chartData && (
    chartData.external_assurance_percent !== undefined ||
    chartData.eu_taxonomy_percent !== undefined ||
    chartData.companies_by_country?.length > 0 ||
    chartData.emissions_profile?.length > 0 ||
    chartData.workforce_and_board_diversity?.length > 0 ||
    chartData.iro_visual_format?.length > 0 || // iro_visual_format is an array of objects
    chartData.esrs_standards_mentioned?.length > 0 ||
    chartData.framework_alignment?.length > 0
  );

  // Check if any data is available to display besides loading state
  const isDataAvailable = hasKpiData || hasChartData;

  if (loading) {
    // It's often better to show a consistent loading state,
    // but DeepDivePage handles a Backdrop. This is a fallback or for granular control.
    // For now, rely on DeepDivePage's loader and show content, or a message if no data.
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Data Overview</Typography>

      {/* KPI Display Section */}
      {hasKpiData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}> {/* Adjusted grid for potentially 3 items */}
            <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>Number of Companies</Typography>
              <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {kpiData.company_count !== undefined ? kpiData.company_count : '-'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}> {/* Adjusted grid for potentially 3 items */}
            <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>Avg. CSRD Page Count</Typography>
              <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {kpiData.average_csrd_page_count !== undefined ? kpiData.average_csrd_page_count.toFixed(1) : '-'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={12} md={4}> {/* Adjusted grid for potentially 3 items, full width on small screens */}
            <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>Avg. Material Topics Count</Typography>
              <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {kpiData.average_material_topics_count !== undefined ? kpiData.average_material_topics_count.toFixed(1) : '-'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts Section */}
      {hasChartData && (
        <CSRDCharts
          externalAssurancePercent={chartData.external_assurance_percent}
          euTaxonomyPercent={chartData.eu_taxonomy_percent}
          companiesByCountry={chartData.companies_by_country}
          emissionsProfile={chartData.emissions_profile}
          workforceAndBoardDiversity={chartData.workforce_and_board_diversity}
          iroVisualFormat={chartData.iro_visual_format}
          esrsStandardsMentioned={chartData.esrs_standards_mentioned}
          frameworkAlignment={chartData.framework_alignment}
        />
      )}

      {!loading && !isDataAvailable && (
         <Typography variant="subtitle1" sx={{mt: 2, textAlign: 'center'}}>
           No data available for the selected filters. Please adjust your filter criteria.
         </Typography>
      )}
    </Box>
  );
};

export default DeepDiveOverview;
